import React from 'react'
import {
  Button,
  Checkbox,
  message,
  Modal,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { ColumnsType } from 'antd/es/table'
import {
  ExclamationCircleOutlined,
  LockOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import { sellerEmployeesApi } from 'api/sellerEmployees'
import { TVariantSeller } from 'models/seller'
import {
  ESellerEmployeeStatus,
  ESellerPermission,
  INotificationSettingsResponse,
  INotificationSettingsTarget,
  ISellerEmployee,
  SELLER_PERMISSION_LABELS,
  TChatNotificationTopic,
  TNotificationChannel,
  TNotificationTopic
} from 'models/sellerEmployees'
import {
  NOTIFICATIONS_BOT_URL,
  TELEGRAM_NOTIFICATIONS_BOT_URL
} from 'shared/constants/urls'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { useSellerAccess } from 'shared/hooks/useSellerAccess'
import { phoneMaskStatic } from 'shared/utils/phoneMaskStatic'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { MainTitle } from 'components/ui'
import { BotConnectModal } from 'components/sellers/BotConnectModal/BotConnectModal'
import { BotDisconnectModal } from 'components/sellers/BotDisconnectModal/BotDisconnectModal'
import styles from './SellerNotifications.module.scss'

type TBotChannel = Extract<TNotificationChannel, 'telegram' | 'max'>
type TCheckboxTopic = TNotificationTopic | TChatNotificationTopic

interface ITopic {
  key: TCheckboxTopic
  title: string
  description: string
  // раздел кабинета, без доступа к которому сотруднику уведомление не приходит
  permission?: ESellerPermission
}

const CHAT_PUSH_HINT =
  'Push-уведомление в приложение HorseSmart приходит всегда, даже если остальные источники отключены'

const PUSH_HINT =
  'Если установлено приложение HorseSmart, вместо SMS придёт пуш. Остальных каналов это не касается: в них уведомление придёт, если они включены.'

const OWNER_VALUE = 'owner'
const SAVED_MESSAGE_KEY = 'seller-notification-settings'
const BOT_POLL_INTERVAL_MS = 3000

const TOPICS: ITopic[] = [
  {
    key: 'new_order',
    title: 'Новые заказы',
    description:
      'Сообщение о поступившем заказе. Ночные заказы по SMS и пушу приходят одной сводкой утром.',
    permission: ESellerPermission.ORDERS
  },
  {
    key: 'stock_reminder',
    title: 'Напоминания об остатках',
    description:
      'Просьба обновить или подтвердить остатки, пока товары ещё в продаже.',
    permission: ESellerPermission.GOODS
  },
  {
    key: 'products_disabled',
    title: 'Отключение товаров',
    description:
      'Товары сняты с витрины из-за неактуальных остатков или ошибки синхронизации.',
    permission: ESellerPermission.GOODS
  },
  {
    key: 'shipping_overdue',
    title: 'Просрочка отправки',
    description:
      'Срок 72 часа на отправку заказа прошёл — заказ будет отменён.',
    permission: ESellerPermission.ORDERS
  },
  {
    key: 'buyer_messages',
    title: 'Сообщения от покупателей',
    description: CHAT_PUSH_HINT,
    permission: ESellerPermission.CHATS
  },
  {
    key: 'support_messages',
    title: 'Сообщения от поддержки',
    description: CHAT_PUSH_HINT
  }
]

const CHANNELS: {
  key: TNotificationChannel
  title: string
  mobileTitle: string
  hint?: string
}[] = [
  { key: 'email', title: 'Email', mobileTitle: 'Email' },
  { key: 'sms', title: 'SMS', mobileTitle: 'SMS', hint: PUSH_HINT },
  { key: 'telegram', title: 'Telegram', mobileTitle: 'TG' },
  { key: 'max', title: 'MAX', mobileTitle: 'MAX' }
]

const BOTS: Record<
  TBotChannel,
  {
    disconnectText: string
    foreignDisconnectText: string
    connected: string
    disconnected: string
  }
> = {
  telegram: {
    disconnectText:
      'Если вы отключите бота, то потеряете доступ к личному кабинету селлера из Telegram и перестанете получать уведомления. Хотите отключить?',
    foreignDisconnectText:
      'потеряет доступ к личному кабинету селлера из Telegram и перестанет получать уведомления. Хотите отключить?',
    connected: 'Telegram-бот подключён',
    disconnected: 'Telegram-бот отключён'
  },
  max: {
    disconnectText:
      'Если вы отключите бота, то перестанете получать уведомления. Хотите отключить?',
    foreignDisconnectText:
      'перестанет получать уведомления. Хотите отключить?',
    connected: 'MAX-бот подключён',
    disconnected: 'MAX-бот отключён'
  }
}

const isBotChannel = (channel: TNotificationChannel): channel is TBotChannel =>
  channel === 'telegram' || channel === 'max'

const getFullName = ({ name, surname }: { name: string; surname: string }) =>
  `${name} ${surname}`.trim()

interface ISellerNotificationsProps {
  type: TVariantSeller
  sellerId: string
}

export const SellerNotifications: React.FC<ISellerNotificationsProps> = ({
  type,
  sellerId
}) => {
  const isProfile = type === 'profile'
  const isMobile = useIsMobile()
  const { isEmployee } = useSellerAccess()
  // Получателя выбирают супер-админ и владелец магазина, сотрудник видит только свои
  const canPickRecipient = !isProfile || !isEmployee
  const [data, setData] = React.useState<INotificationSettingsResponse | null>(
    null
  )
  const [isLoading, setIsLoading] = React.useState(true)
  const [isSaving, setIsSaving] = React.useState(false)
  const [employees, setEmployees] = React.useState<ISellerEmployee[]>([])
  const [employeeId, setEmployeeId] = React.useState<string | null>(null)
  const isOwnSettings = isProfile && !employeeId
  // Ссылка на бота привязывает мессенджер того, кто по ней перешёл, поэтому владелец
  // мессенджер сотрудника не трогает. Супер-админ подключает, передав QR продавцу
  const canManageBots = !isProfile || !employeeId
  const [ownerName, setOwnerName] = React.useState('')
  const [connectChannel, setConnectChannel] =
    React.useState<TBotChannel | null>(null)
  const [disconnectChannel, setDisconnectChannel] =
    React.useState<TBotChannel | null>(null)
  // Номер последнего запроса: ответ применяем, только если после него не было других
  const requestRef = React.useRef(0)

  // В своём кабинете магазин бэкенд берёт из токена
  const target = React.useMemo<INotificationSettingsTarget>(
    () => ({
      seller_id: isProfile ? undefined : sellerId,
      employee_id: employeeId ?? undefined
    }),
    [isProfile, sellerId, employeeId]
  )

  const read = async (isSilent: boolean) => {
    const requestId = ++requestRef.current
    try {
      if (!isSilent) setIsLoading(true)
      const { data: response } =
        await sellerEmployeesApi.getNotificationSettings(target)
      if (requestId !== requestRef.current) return
      setData(response)
      if (response.target.type === 'OWNER') {
        setOwnerName(getFullName(response.target))
      }
    } catch (e) {
      if (!isSilent && requestId === requestRef.current) showServerError(e)
    } finally {
      if (requestId === requestRef.current) setIsLoading(false)
    }
  }

  const mutate = async (
    request: () => Promise<{ data: INotificationSettingsResponse }>
  ) => {
    requestRef.current++
    try {
      setIsSaving(true)
      const { data: response } = await request()
      setData(response)
      return true
    } catch (e) {
      showServerError(e)
      return false
    } finally {
      // чтение, начатое во время сохранения, могло успеть до записи на сервере
      requestRef.current++
      setIsSaving(false)
    }
  }

  React.useEffect(() => {
    read(false)
  }, [target])

  React.useEffect(() => {
    if (!canPickRecipient) return

    // Настройки есть только у работающих: приглашённый ещё не принял приглашение
    sellerEmployeesApi
      .getEmployees({ seller_id: isProfile ? undefined : sellerId })
      .then(({ data: list }) =>
        setEmployees(
          list.filter(({ status }) => status === ESellerEmployeeStatus.ACTIVE)
        )
      )
      .catch(showServerError)
  }, [canPickRecipient, isProfile, sellerId])

  // Бота подключают в мессенджере, вне этой вкладки: статус перечитываем при возврате
  // на неё и пока открыто окно с QR-кодом
  React.useEffect(() => {
    if (!isProfile) return

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') read(true)
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [isProfile, target])

  React.useEffect(() => {
    if (!connectChannel) return

    const interval = setInterval(() => read(true), BOT_POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [connectChannel, target])

  const isBotConnected = (channel: TBotChannel) =>
    channel === 'telegram'
      ? Boolean(data?.contacts.telegram_connected)
      : Boolean(data?.contacts.max_connected)

  // Почта и SMS уходят на указанные адрес и номер, даже неподтверждённые.
  // Сохранённый выбор не сбрасываем: он вернётся, когда канал подключат
  const isChannelReady = (channel: TNotificationChannel) => {
    if (isBotChannel(channel)) return isBotConnected(channel)
    if (channel === 'email') return Boolean(data?.contacts.email)
    return Boolean(data?.contacts.phone)
  }

  React.useEffect(() => {
    if (!connectChannel || !isBotConnected(connectChannel)) return

    setConnectChannel(null)
    showMessage('success', BOTS[connectChannel].connected, '')
  }, [data, connectChannel])

  const getBotUrl = (channel: TBotChannel) => {
    const token = data?.bot?.bot_api_token
    if (channel === 'telegram') {
      return token ? `${TELEGRAM_NOTIFICATIONS_BOT_URL}?start=${token}` : null
    }
    if (data?.bot?.max_bot_link) return data.bot.max_bot_link
    return token && NOTIFICATIONS_BOT_URL
      ? `${NOTIFICATIONS_BOT_URL}?start=${token}`
      : null
  }

  const isTopicEnabled = (channel: TNotificationChannel, topic: TCheckboxTopic) =>
    Boolean(data?.settings[channel]?.[topic])

  const saveTopic = async (
    channel: TNotificationChannel,
    topic: TCheckboxTopic,
    value: boolean
  ) => {
    if (!data) return

    const previous = data.settings
    const next = {
      ...previous,
      [channel]: { ...previous[channel], [topic]: value }
    }
    setData({ ...data, settings: next })

    const isSaved = await mutate(() =>
      sellerEmployeesApi.saveNotificationSettings(next, target)
    )
    if (isSaved) {
      message.success({
        content: 'Настройки сохранены',
        key: SAVED_MESSAGE_KEY
      })
    } else {
      setData((current) => current && { ...current, settings: previous })
    }
  }

  const getRecipientText = () => {
    if (isOwnSettings) return 'вы вовремя узнавали'
    return data?.target.type === 'EMPLOYEE'
      ? 'сотрудник вовремя узнавал'
      : 'продавец вовремя узнавал'
  }

  // без уведомления о заказе о нём узнают, только зайдя в кабинет
  const toggleTopic = (
    channel: TNotificationChannel,
    topic: TCheckboxTopic,
    value: boolean
  ) => {
    if (value || topic !== 'new_order') return saveTopic(channel, topic, value)

    Modal.confirm({
      centered: true,
      title: 'Отключить уведомления о новых заказах?',
      content: `Эти уведомления нужны, чтобы ${getRecipientText()} о новых заказах. Действительно хотите отключить?`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Да',
      cancelText: 'Нет',
      onOk: () => saveTopic(channel, topic, value)
    })
  }

  const disconnect = async () => {
    if (!disconnectChannel) return

    const isDisconnected = await mutate(() =>
      sellerEmployeesApi.disconnectBot(disconnectChannel, target)
    )
    if (isDisconnected) {
      showMessage('success', BOTS[disconnectChannel].disconnected, '')
      setDisconnectChannel(null)
    }
  }

  const getPhoneHint = () => {
    if (data?.target.type === 'EMPLOYEE') {
      return 'Телефон указывается и подтверждается в личном кабинете на сайте HorseSmart, раздел «Личные данные»'
    }
    return 'Номер меняется и подтверждается в разделе «Основная информация»'
  }

  const renderContactStatus = (
    contact: string | null,
    isVerified: boolean,
    emptyText: string,
    hint?: string
  ) => {
    const tag = !contact ? (
      <Tag className={styles.statusTag}>{emptyText}</Tag>
    ) : (
      <Tag
        color={isVerified ? 'success' : 'warning'}
        className={styles.statusTag}
      >
        {isVerified ? 'Подтверждено' : 'Не подтверждено'}
      </Tag>
    )

    return (
      <>
        <div className={styles.statusDetail}>
          {contact && (
            <span className={styles.contact} title={contact}>
              {contact}
            </span>
          )}
        </div>
        {hint && (!contact || !isVerified) ? (
          <Tooltip title={hint}>{tag}</Tooltip>
        ) : (
          tag
        )}
      </>
    )
  }

  const getDisconnectText = (channel: TBotChannel) => {
    if (isOwnSettings) return BOTS[channel].disconnectText
    const recipient =
      data?.target.type === 'EMPLOYEE' ? 'Сотрудник' : 'Продавец'
    return `${recipient} ${BOTS[channel].foreignDisconnectText}`
  }

  const renderBotStatus = (channel: TBotChannel) => {
    const isConnected = isBotConnected(channel)
    const botUrl = getBotUrl(channel)
    const foreignBotHint = canManageBots
      ? ''
      : 'Мессенджер подключает и отключает сам сотрудник'

    return (
      <>
        <div className={styles.statusDetail}>
          {isConnected ? (
            <Tooltip title={foreignBotHint}>
              <Button
                size='small'
                className={styles.disconnectButton}
                disabled={!canManageBots || isSaving}
                onClick={() => setDisconnectChannel(channel)}
              >
                Отключить
              </Button>
            </Tooltip>
          ) : (
            <Tooltip
              title={
                foreignBotHint || (botUrl ? '' : 'Ссылка на бота недоступна')
              }
            >
              <Button
                type='primary'
                size='small'
                disabled={!canManageBots || !botUrl}
                onClick={() => setConnectChannel(channel)}
              >
                Подключить
              </Button>
            </Tooltip>
          )}
        </div>
        <Tag
          color={isConnected ? 'success' : undefined}
          className={styles.statusTag}
        >
          {isConnected ? 'Подключен' : 'Не подключен'}
        </Tag>
      </>
    )
  }

  const renderChannelStatus = (channel: TNotificationChannel) => {
    if (!data) return null

    const { contacts } = data
    let status: React.ReactNode
    if (isBotChannel(channel)) {
      status = renderBotStatus(channel)
    } else if (channel === 'email') {
      status = renderContactStatus(
        contacts.email,
        contacts.email_verified,
        'Не указана'
      )
    } else {
      status = renderContactStatus(
        contacts.phone ? phoneMaskStatic(contacts.phone) : null,
        contacts.phone_verified,
        'Не указан',
        getPhoneHint()
      )
    }

    return <div className={styles.channelStatus}>{status}</div>
  }

  const renderChannelTitle = (title: string, hint?: string) => (
    <span>
      {title}
      {hint && (
        <Tooltip title={hint}>
          <QuestionCircleOutlined className={styles.helpIcon} />
        </Tooltip>
      )}
    </span>
  )

  const columns: ColumnsType<ITopic> = [
    {
      title: 'Тип уведомления',
      dataIndex: 'title',
      render: (_, { key, title, description, permission }) => {
        const lastWordStart = title.lastIndexOf(' ') + 1
        return (
          <>
            {title.slice(0, lastWordStart)}
            {/* неразрывный пробел перед значком браузер игнорирует, держим его в одной строке с последним словом */}
            <span className={styles.lastWord}>
              {title.slice(lastWordStart)}
              <Tooltip title={description}>
                <QuestionCircleOutlined className={styles.helpIcon} />
              </Tooltip>
              {data && permission && !data.available_topics.includes(key) && (
                <Tooltip
                  title={`Приходит только при доступе к разделу «${SELLER_PERMISSION_LABELS[permission]}»`}
                >
                  <LockOutlined className={styles.helpIcon} />
                </Tooltip>
              )}
            </span>
          </>
        )
      }
    },
    ...CHANNELS.map(({ key: channel, title, mobileTitle, hint }) => ({
      title: isMobile ? (
        mobileTitle
      ) : (
        <div className={styles.channelHead}>
          {renderChannelTitle(title, hint)}
          {renderChannelStatus(channel)}
        </div>
      ),
      dataIndex: channel,
      align: 'center' as const,
      width: isMobile ? 52 : 140,
      render: (_: unknown, { key, title: topicTitle }: ITopic) => {
        const isAvailable =
          Boolean(data?.available_topics.includes(key)) &&
          isChannelReady(channel)

        return (
          <Checkbox
            aria-label={`${topicTitle}: ${title}`}
            checked={isAvailable && isTopicEnabled(channel, key)}
            disabled={!isAvailable || isLoading || isSaving}
            onChange={(e) => toggleTopic(channel, key, e.target.checked)}
          />
        )
      }
    }))
  ]

  const getOwnerLabel = () => {
    const owner = isProfile ? 'Вы' : 'Владелец'
    return ownerName ? `${owner} — ${ownerName}` : owner
  }

  const connectUrl = connectChannel ? getBotUrl(connectChannel) : null

  return (
    <div className={styles.wrapper}>
      <MainTitle text='Уведомления' hideOnMobile />
      {canPickRecipient && (
        <div className={styles.target}>
          <Typography.Text>Получатель</Typography.Text>
          <Select
            className={styles.targetSelect}
            size='large'
            value={employeeId ?? OWNER_VALUE}
            disabled={isSaving}
            options={[
              {
                value: OWNER_VALUE,
                label: getOwnerLabel()
              },
              ...employees.map((employee) => ({
                value: employee._id,
                label: getFullName(employee)
              }))
            ]}
            onChange={(value) =>
              setEmployeeId(value === OWNER_VALUE ? null : value)
            }
          />
        </div>
      )}
      {isMobile && data && (
        <div className={styles.channelList}>
          {CHANNELS.map(({ key, title, hint }) => (
            <div key={key} className={styles.channelRow}>
              <span className={styles.channelName}>
                {renderChannelTitle(title, hint)}
              </span>
              {renderChannelStatus(key)}
            </div>
          ))}
        </div>
      )}
      <Table
        className={styles.table}
        scroll={isMobile ? undefined : { x: 720 }}
        size={isMobile ? 'small' : undefined}
        tableLayout={isMobile ? 'fixed' : undefined}
        columns={columns}
        dataSource={TOPICS}
        loading={isLoading}
        rowKey={(el) => el.key}
        pagination={false}
      />
      {connectChannel && connectUrl && (
        <BotConnectModal
          open
          botUrl={connectUrl}
          messenger={connectChannel}
          onClose={() => setConnectChannel(null)}
        />
      )}
      <BotDisconnectModal
        open={Boolean(disconnectChannel)}
        text={disconnectChannel ? getDisconnectText(disconnectChannel) : ''}
        isLoading={isSaving}
        onConfirm={disconnect}
        onCancel={() => setDisconnectChannel(null)}
      />
    </div>
  )
}
